import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { fCurrency } from 'src/utils/format-number';
import { accountDisplayName } from 'src/utils/account-labels';

import Image from 'src/components/image';
import Iconify from 'src/components/iconify';
import TextMaxLine from 'src/components/text-max-line';

// How many shops are named before the rest become a single "+N" chip. Two is enough to see that
// an item is in more than one place without the chips taking the row over.
const MAX_ACCOUNT_CHIPS = 2;

// The picture on a compact row. Big enough to recognise the item, small enough that a screenful
// of them is a list a seller can run their eye down.
const COMPACT_IMAGE_SIZE = 48;

// A SKU this long already says which item it is; the rest would push the price off the row.
const MAX_SKU_CHARS = 16;

/**
 * The item's first picture, whichever shape the item came in: a scan's summary carries `images`,
 * a row handed over from the inventory table carries `image`.
 */
export function firstImage(listing) {
  return listing?.images?.[0] || listing?.image || null;
}

/** True when a placement is a real post on a marketplace, by item id or by offer id. */
const holdsPost = (placement) => Boolean(placement?.platformListingId || placement?.platformOfferId);

/**
 * The shops this item is posted on, in the words the seller uses for them.
 *
 * With one connected shop on a marketplace, the marketplace's name says everything — "eBay". With
 * two eBay shops, "eBay" says nothing about which of them a listing is on, so the shop's own name
 * is shown too. Until the connected shops have been loaded, the name is shown whenever there is
 * one: too much detail beats naming the wrong shop.
 */
export function useAccountNames(listing) {
  const marketplaces = useSelector((state) => state.management.marketplaces);

  return useMemo(() => {
    const shopsOn = (platform) =>
      (marketplaces || []).filter((row) => row.connected && row.platform === platform).length;

    const names = [];
    (listing?.platformAccounts || []).filter(holdsPost).forEach((placement) => {
      const label = shopsOn(placement.platform) === 1 ? null : placement.integration?.label;
      const name = accountDisplayName(placement.platform, label);
      if (name && !names.includes(name)) names.push(name);
    });

    return names;
  }, [listing, marketplaces]);
}

/** The item's picture, or a plain grey square when it has none. */
export function ListingThumbnail({ src, alt, size, height, rounded }) {
  const shape = {
    display: 'block',
    width: size ?? '100%',
    height: height ?? size,
    borderRadius: rounded,
    flexShrink: 0,
  };

  // `visibleByDefault` loads the picture straight away instead of waiting to be scrolled into
  // view: these are thumbnails in short lists, and inside a dialog the scroll watcher never fires,
  // which used to leave every picture a grey placeholder for good.
  if (src) return <Image visibleByDefault src={src} alt={alt} sx={shape} />;

  return (
    <Box
      sx={{
        ...shape,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.neutral',
      }}
    >
      <Iconify icon="solar:gallery-outline" width={size ? 20 : 28} sx={{ color: 'text.disabled' }} />
    </Box>
  );
}

ListingThumbnail.propTypes = {
  alt: PropTypes.string,
  src: PropTypes.string,
  size: PropTypes.number,
  height: PropTypes.number,
  rounded: PropTypes.number,
};

/**
 * One item as two quiet lines of text and nothing else: the title, then what a seller needs to
 * tell it apart from its namesakes — SKU, price, and the shops it is on. For the long list they
 * run an eye down while picking, where pictures and chips on every row add up to noise.
 */
export function ListingTextLine({ listing, secondLine }) {
  const title = listing?.title || 'Untitled item';
  const accountNames = useAccountNames(listing);
  const price = fCurrency(listing?.price, listing?.currency);
  const sku = listing?.sku
    ? `SKU ${listing.sku.length > MAX_SKU_CHARS ? `${listing.sku.slice(0, MAX_SKU_CHARS)}…` : listing.sku}`
    : null;
  const details = [sku, price || null, ...accountNames].filter(Boolean).join(' · ');

  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" noWrap title={title}>
        {title}
      </Typography>

      {secondLine ||
        (details ? (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block' }}
            title={listing?.sku ? `SKU ${listing.sku}` : undefined}
          >
            {details}
          </Typography>
        ) : null)}
    </Box>
  );
}

ListingTextLine.propTypes = {
  listing: PropTypes.object,
  // Rendered in place of the usual SKU · price · shops line — for a row that needs to say
  // something about itself instead (like why it can't be picked right now).
  secondLine: PropTypes.node,
};

/** The shops as chips, with anything past the first couple counted in a chip of its own. */
function AccountChips({ names }) {
  if (names.length === 0) return null;

  const shown = names.slice(0, MAX_ACCOUNT_CHIPS);
  const hidden = names.slice(MAX_ACCOUNT_CHIPS);

  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, minWidth: 0 }}>
      {shown.map((name) => (
        <Chip key={name} size="small" variant="soft" label={name} sx={{ height: 22, maxWidth: 180 }} />
      ))}

      {hidden.length > 0 ? (
        <Tooltip title={hidden.join(', ')}>
          <Chip size="small" variant="soft" label={`+${hidden.length}`} sx={{ height: 22 }} />
        </Tooltip>
      ) : null}
    </Stack>
  );
}

AccountChips.propTypes = {
  names: PropTypes.array.isRequired,
};

/**
 * One item as the merge pages show it: its picture, title, SKU and price, and the shops it is
 * posted on. Nothing here is interactive — whatever wraps it (a duplicate card, a pickable row in
 * the list) owns the controls, so this only ever has one job: show what the item is.
 *
 * Two layouts, the same facts in both:
 *   'row'     — title over two lines, shops underneath, for the cards that show a whole merge.
 *   'compact' — everything on one line, shops to the right, for the long list a seller scans.
 */
export default function ListingRow({ listing, variant = 'row', imageSize = 56 }) {
  const title = listing?.title || 'Untitled item';
  const accountNames = useAccountNames(listing);
  const price = fCurrency(listing?.price, listing?.currency);
  const sku = listing?.sku
    ? `SKU ${listing.sku.length > MAX_SKU_CHARS ? `${listing.sku.slice(0, MAX_SKU_CHARS)}…` : listing.sku}`
    : null;
  const details = [sku, price || null].filter(Boolean).join(' · ');

  const caption = details ? (
    <Typography
      variant="caption"
      color="text.secondary"
      noWrap
      sx={{ display: 'block' }}
      title={listing?.sku ? `SKU ${listing.sku}` : undefined}
    >
      {details}
    </Typography>
  ) : null;

  if (variant === 'compact') {
    return (
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ minWidth: 0, flex: 1, flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <ListingThumbnail src={firstImage(listing)} alt={title} size={COMPACT_IMAGE_SIZE} rounded={1} />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" noWrap title={title}>
            {title}
          </Typography>

          {caption}
        </Box>

        <AccountChips names={accountNames} />
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      <ListingThumbnail src={firstImage(listing)} alt={title} size={imageSize} rounded={1} />

      <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <TextMaxLine variant="body2" line={2} title={title}>
          {title}
        </TextMaxLine>

        {caption}

        <AccountChips names={accountNames} />
      </Stack>
    </Stack>
  );
}

ListingRow.propTypes = {
  listing: PropTypes.shape({
    title: PropTypes.string,
    sku: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    image: PropTypes.string,
    images: PropTypes.array,
    platformAccounts: PropTypes.array,
  }),
  variant: PropTypes.oneOf(['row', 'compact']),
  imageSize: PropTypes.number,
};
